
import { jitsiLocalStorage } from '@jitsi/js-utils';
import { sha256 } from 'js-sha256';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SpeechRecognition, {
    useSpeechRecognition
} from 'react-speech-recognition';
import styled from 'styled-components';

import { reduceAllUserVolumeTemporarily } from '../../../base/conference';
import { apiKEY, mtDomain } from '../../../base/config/constants';
import { Dialog, closeDialogs, isAnyDialogOpen } from '../../../base/dialog';
import {
    AnimatedSVG,
    Icon,
    IconHelp,
    IconMicrophone
} from '../../../base/icons';
import { setVoiceCommandString } from '../../../base/mtApi/actions';
import { fetchAxiosApi } from '../../../base/mtApi/functions';
import { getLocalVideoTrack } from '../../../base/tracks';
import logger from '../../../local-recording/logger';
import { findMatchingVoiceCommand } from '../../constants';

import { WebMediaRecorderAdapter } from './WebMediaRecorderAdapter';

/**
 * Custom voice recording class that directly accesses the user's microphone
 * without going through the conference audio system
 */
export class VoiceRecorder {
    /**
     * Creates a new instance of VoiceRecorder.
     * Initializes the media recorder, stream, and recorded chunks storage.
     */
    constructor() {
        this.mediaRecorder = null;
        this.stream = null;
        this.recordedChunks = [];
        this._stopPromise = null;
    }

    /**
     * Starts the voice recording process.
     * @returns {Promise<void>} Resolves when recording starts successfully.
     */
    async start() {
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            return;
        }

        this.stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            },
            video: false
        });

        this.recordedChunks = [];

        // Choose a supported mimeType
        const preferred = 'audio/webm;codecs=opus';
        const fallback = 'audio/webm';
        const mimeType = MediaRecorder.isTypeSupported?.(preferred) ? preferred
            : MediaRecorder.isTypeSupported?.(fallback) ? fallback : '';

        this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);

        this.mediaRecorder.ondataavailable = event => {
            if (event.data && event.data.size > 0) {
                this.recordedChunks.push(event.data);
            }
        };

        this.mediaRecorder.start(); // sync
        logger?.log?.('Voice recording started with direct microphone access');
    }

    /**
     * Stops the voice recording process.
     * @returns {Promise<Blob>} Resolves with the recorded audio blob.
     */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async stop() {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
            throw new Error('No active recording');
        }

        if (this._stopPromise) {
            return this._stopPromise;
        } // prevent double stop

        this._stopPromise = new Promise((resolve, reject) => {
            const cleanupAll = () => {
                if (this.stream) {
                    this.stream.getTracks().forEach(t => t.stop());
                    this.stream = null;
                }
                this.mediaRecorder = null;
            };

            let settled = false;

            this.mediaRecorder.onstop = () => {
                try {
                    const type = this.recordedChunks.length
                        ? this.recordedChunks[0].type || 'audio/webm'
                        : 'audio/webm';
                    const audioBlob = new Blob(this.recordedChunks, { type });

                    this.recordedChunks = [];
                    cleanupAll();
                    logger?.log?.('Voice recording stopped');
                    settled = true;
                    resolve(audioBlob);
                } catch (err) {
                    cleanupAll();
                    settled = true;
                    reject(err);
                }
            };

            this.mediaRecorder.onerror = e => {
                if (!settled) {
                    cleanupAll();
                    settled = true;
                    reject(e.error || e);
                }
            };

            // Ensure the final chunk is flushed before stopping
            try {
                this.mediaRecorder.requestData?.();
            } catch { /* empty */ }
            this.mediaRecorder.stop();

            // Safety net in case onstop never fires on some browsers
            setTimeout(() => {
                if (!settled) {
                    try {
                        const type = this.recordedChunks.length
                            ? this.recordedChunks[0].type || 'audio/webm'
                            : 'audio/webm';
                        const audioBlob = new Blob(this.recordedChunks, { type });

                        this.recordedChunks = [];
                        cleanupAll();
                        settled = true;
                        resolve(audioBlob);
                    } catch (err) {
                        cleanupAll();
                        settled = true;
                        reject(err);
                    }
                }
            }, 1500);
        });

        return this._stopPromise.finally(() => {
            this._stopPromise = null;
        });
    }

    /**
     * Cleans up resources used by the recorder.
     */
    cleanup() {
        if (this.stream) {
            this.stream.getTracks().forEach(t => t.stop());
            this.stream = null;
        }
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            try {
                this.mediaRecorder.stop();
            } catch {
                // Ignore errors when stopping media recorder
            }
        }
        this.mediaRecorder = null;
        this.recordedChunks = [];
        this._stopPromise = null;
    }
}

// export const checkText: Object<String> = {
//     'DonorBox': 'Open donorbox',
//     'clicknPledge': 'Open click and pledge',
//     'Recording': 'Open recording',
//     'Livestream': 'Open livestream',
//     'WhiteBoard': 'Open whiteboard'
// };

const DIVs = styled.div`
  padding: 10px;
`;

const Ps = styled.p`
  font-size: 15px;
  font-family: "Poppins";
`;

const StyledDiv = styled.div`
    margin-top: 10px;
`;

export const VoiceCommandDialog = () => {
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();
    const isSpeechRecognitionSupported = Boolean(browserSupportsSpeechRecognition);
    const adapterRef = useRef(new WebMediaRecorderAdapter());
    const selectedMicId = undefined; // or a deviceId string from enumerateDevices
    const { t } = useTranslation();
    const tooltipRef = useRef(null);
    const [ open, setOpen ] = useState(false);
    const hints = t('voicecommand.hints', {
        lng: 'en',
        returnObjects: true
    });

    const [ listen, setListen ] = useState(false);
    const { accessToken } = useSelector(state => state['features/base/jwt']);
    const userAccessToken = jitsiLocalStorage.getItem('accessToken');
    const config = useSelector(state => state['features/base/config']);
    const accesTokn = accessToken ?? userAccessToken;
    const [ loader, setLoader ] = React.useState(false);
    const [ record, setRecorder ] = React.useState(false);
    const [ resData, setResData ] = useState('');
    const dispatch = useDispatch();
    const localVideo = useSelector(state =>
        getLocalVideoTrack(state['features/base/tracks'])
    );
    const store = useSelector(state => state);
    const isMounted = React.useRef(false);
    const prevListeningRef = useRef(false);
    const listenTimeoutRef = useRef(null);

    // Local-only recording adapter for voice commands
    const [ localRecorder, setLocalRecorder ] = React.useState(null);

    // tempmuted from store
    const tempMuted = useSelector(state => state['features/base/conference'].tempMuted);

    // Apply ducking
    const tempMuteUsers = React.useCallback(() => {
        if (!tempMuted) {
            dispatch(reduceAllUserVolumeTemporarily());
        }
    }, [ dispatch, tempMuted ]);

    // Remove ducking (restore volumes)
    const unMuteUsers = React.useCallback(() => {
        if (tempMuted) {
            dispatch(reduceAllUserVolumeTemporarily());
        }
    }, [ dispatch, tempMuted ]);

    useEffect(() => {
        if (
            localVideo
            && localVideo.videoType === 'desktop'
            && isMounted.current === true
        ) {
            if (isAnyDialogOpen(store)) {
                dispatch(closeDialogs(true));
            }
        }
    }, [ localVideo, dispatch, store ]);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
            if (listenTimeoutRef.current) {
                clearTimeout(listenTimeoutRef.current);
            }
            try {
                SpeechRecognition.stopListening();
            } catch { /* ignore */ }
            if (localRecorder) {
                localRecorder.cleanup();
            }
            unMuteUsers();
        };
    }, []);

    // Briefly show the recognized command, then close this dialog BEFORE
    // running it: commands that open a dialog (virtual background, settings…)
    // would otherwise be closed again by our own closeDialogs.
    const runCommandAfterClose = React.useCallback(key => {
        setTimeout(() => {
            if (isMounted.current) {
                dispatch(closeDialogs(true));
            }
            dispatch(setVoiceCommandString(key));
        }, 600);
    }, [ dispatch ]);

    const processCommand = React.useCallback(text => {
        if (!text || typeof text !== 'string' || !text.trim()) {
            return false;
        }

        const match = findMatchingVoiceCommand(text.trim());

        if (match) {
            if (listenTimeoutRef.current) {
                clearTimeout(listenTimeoutRef.current);
                listenTimeoutRef.current = null;
            }

            try {
                SpeechRecognition.stopListening();
            } catch { /* ignore */ }

            setListen(false);
            setLoader(false);
            unMuteUsers();
            setResData(match.phrase || text);

            resetTranscript();
            runCommandAfterClose(match.key);

            return true;
        }

        return false;
    }, [ resetTranscript, runCommandAfterClose, unMuteUsers ]);

    // Matching waits for the final transcript: an interim "open" already
    // fuzzy-matches "open chat" before "virtual background" is heard.
    // Handle speech recognition completion cleanly when listening stops
    useEffect(() => {
        const wasListening = prevListeningRef.current;

        prevListeningRef.current = listening;

        if (wasListening && !listening) {
            if (listenTimeoutRef.current) {
                clearTimeout(listenTimeoutRef.current);
                listenTimeoutRef.current = null;
            }

            setListen(false);
            unMuteUsers();

            const currentTranscript = transcript ? transcript.trim() : '';

            if (currentTranscript) {
                const matched = processCommand(currentTranscript);

                if (!matched) {
                    resetTranscript();
                    setResData('The Command does not match what you are looking for');
                }
            } else {
                setLoader(false);
            }
        }
    }, [ listening, transcript, resetTranscript, unMuteUsers, processCommand ]);

    // Helper function to start local-only recording using direct microphone access
    const startLocalRecording = async () => {
        try {
            await adapterRef.current.start(selectedMicId);
        } catch (error) {
            logger.error('Error starting local recording:', error);
            throw error;
        }
    };

    // Helper function to stop local recording and get the audio data
    const stopLocalRecording = async () => {
        try {
            const { blob } = await adapterRef.current.stop();

            setLocalRecorder(null);

            return blob;
        } catch (error) {
            logger.error('Error stopping local recording:', error);
            setLocalRecorder(null);
            throw error;
        }
    };

    // Update the transcribeBlob function to play the blob after processing
    const transcribeBlob = async audioBlob => {
        setLoader(true);

        try {
            // Convert the audio blob to a file for the API
            const file = new File([ audioBlob ], 'microphone.webm', {
                type: 'audio/webm',
                lastModified: new Date().getTime()
            });

            const moement = moment().seconds(0)
                .milliseconds(0)
                .format('X');

            const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

            const formData = new FormData();

            formData.append('audioFile', file);
            formData.append('client_id', config.MH_CLIENT_ID);
            formData.append('credentials', mesh256);
            formData.append('domain', mtDomain);

            const apiconfig = {
                headers: {
                    'content-type': 'multipart/form-data',
                    Authorization: `Bearer ${accesTokn}`
                }
            };
            const path = 'speechtotext';

            const response = await fetchAxiosApi(path, {
                method: 'post',
                body: formData,
                headers: apiconfig.headers
            });

            if (response && response.transcript) {
                setResData(response.transcript);

                const match = findMatchingVoiceCommand(response.transcript);

                if (match) {
                    setLoader(false);
                    runCommandAfterClose(match.key);

                    return {
                        success: true,
                        transcript: response.transcript,
                        command: match.key
                    };
                }
            }

            setLoader(false);

            return {
                success: false,
                transcript: response?.transcript || ''
            };

        } catch (error) {
            logger.error('Error transcribing audio:', error);
            setLoader(false);

            return {
                success: false,
                error: error.message
            };
        }
    };

    const onListenClick = async e => {
        e.preventDefault();
        if (listening || listen) {
            return;
        }

        setResData('');
        setListen(true);

        if (isSpeechRecognitionSupported) {
            logger.log('Starting voice command listening...', listening);
            resetTranscript();
            tempMuteUsers();
            SpeechRecognition.startListening({ continuous: false });

            if (listenTimeoutRef.current) {
                clearTimeout(listenTimeoutRef.current);
            }
            listenTimeoutRef.current = setTimeout(() => {
                SpeechRecognition.stopListening();
                setListen(false);
                unMuteUsers();
            }, 8000);
        } else {
            setRecorder(true);
            tempMuteUsers();

            try {
                await startLocalRecording();

                if (listenTimeoutRef.current) {
                    clearTimeout(listenTimeoutRef.current);
                }
                listenTimeoutRef.current = setTimeout(async () => {
                    try {
                        const audioData = await stopLocalRecording();
                        const result = await transcribeBlob(audioData);

                        setListen(false);
                        setRecorder(false);
                        unMuteUsers();

                        if (!result || !result.success) {
                            setResData('The Command does not match what you are looking for');
                        }
                    } catch (error) {
                        logger.error('Error processing audio fallback:', error);
                        setListen(false);
                        setRecorder(false);
                        unMuteUsers();
                    }
                }, 4000);
            } catch (error) {
                logger.error('Failed to start fallback recorder:', error);
                setListen(false);
                setRecorder(false);
                unMuteUsers();
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = event => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleClose = e => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        if (listenTimeoutRef.current) {
            clearTimeout(listenTimeoutRef.current);
        }
        try {
            SpeechRecognition.stopListening();
        } catch { /* ignore */ }
        unMuteUsers();
        dispatch(closeDialogs(true)); // Close the dialog when the cross button is clicked
    };

    return (
        <Dialog
            disableBlanketClickDismiss = { true }
            disableOverlay = { true }
            onClose = { handleClose } // Handle dialog close
            submitDisabled = { true }
            titleKey = { t('voicecommand.vcLabel') }
            width = 'medium'>
            <DIVs className = 'voice-app'>
                <StyledDiv
                    className = 'help-tip'
                    innerRef = { tooltipRef }
                    onClick = { () => setOpen(prev => !prev) }>
                    <Icon
                        alt = 'help'
                        size = { 16 }
                        src = { IconHelp } />
                    {open && (
                        <ol className = 'help-tip-list'>
                            {Object.entries(hints).map(([ , value ], idx) =>
                                <li key = { idx }>{`${value}`}</li>
                            )}
                        </ol>)
                    }
                </StyledDiv>
                <h2 className = 'voice-command-header'>{t('voicecommand.voiceCommandForMeethour')}</h2>
                <h3>{t('voicecommand.clickOnMic')}</h3>
                <button
                    className = 'btn-mic mb-4 mt-4'
                    id = 'speech'
                    onClick = { onListenClick }>
                    {listen || listening ? <div className = 'pulse-ring' /> : ''}
                    <Icon
                        alt = 'microphone'
                        size = { 50 }
                        src = { loader ? AnimatedSVG : IconMicrophone } />
                </button>
                {!isSpeechRecognitionSupported && record
                    && _.isEmpty(resData)
                    && <h4>{t('voicecommand.pleaseWaitWeAreRecording')}</h4>
                }
                <Ps>{transcript || resData}</Ps>
                <ul id = 'playlist' />
            </DIVs>
        </Dialog>
    );
};

